import { useMemo } from 'react';

export type CommunicationChannelType = 'email' | 'sms' | 'mobil' | 'festnetz';

export type PersonCommunicationChannel = {
  id?: number;
  type: CommunicationChannelType;
  value: string;
  isPrimary?: boolean;
  officeContactAllowed?: boolean;
  isActive?: boolean;
  source?: string;
};

const normalizeType = (raw: string): CommunicationChannelType | null => {
  const type = String(raw || '').toLowerCase().trim();
  if (type === 'email' || type === 'email_alt') return 'email';
  if (type === 'sms') return 'sms';
  if (type === 'mobil' || type === 'mobile' || type === 'phone') return 'mobil';
  if (type === 'festnetz' || type === 'telefon') return 'festnetz';
  return null;
};

export function usePersonCommunicationChannels(value: unknown) {
  return useMemo(() => {
    if (!Array.isArray(value)) {
      return [];
    }
    const mapped: PersonCommunicationChannel[] = [];
    for (const entry of value) {
      if (!entry || typeof entry !== 'object') continue;
      const raw = entry as Record<string, unknown>;
      const type = normalizeType(String(raw.type ?? raw.channel_type ?? ''));
      const channelValue = String(raw.value ?? raw.channel_value ?? '').trim();
      if (!type || !channelValue) continue;
      mapped.push({
        id: typeof raw.id === 'number' ? raw.id : undefined,
        type,
        value: channelValue,
        isPrimary: raw.isPrimary === true || raw.is_primary === 1,
        officeContactAllowed: raw.officeContactAllowed !== false && raw.office_contact_allowed !== 0,
        isActive: raw.isActive !== false && raw.is_active !== 0,
        source: typeof raw.source === 'string' ? raw.source : undefined,
      });
    }
    return mapped;
  }, [value]);
}

export const derivePrimaryEmailFromChannels = (channels: PersonCommunicationChannel[]): string => {
  const primary = channels.find((c) => c.type === 'email' && c.isPrimary && c.value.trim() !== '');
  if (primary) return primary.value;
  const first = channels.find((c) => c.type === 'email' && c.value.trim() !== '');
  return first?.value || '';
};
